<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  board: { type: Object, default: null },
  inboxes: { type: Array, default: () => [] },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits([
  'close',
  'saveBoard',
  'createStage',
  'updateStage',
  'deleteStage',
]);

const { t } = useI18n();

const boardName = ref('');
const boardInboxId = ref('');
const stages = ref([]);

const isEditing = computed(() => !!props.board?.id);
const title = computed(() =>
  isEditing.value
    ? t('KANBAN.BOARD.MODAL_EDIT_TITLE')
    : t('KANBAN.BOARD.MODAL_CREATE_TITLE')
);

const stagePromotionOptions = computed(() => [
  { value: '', label: t('KANBAN.BOARD.STAGE_PROMOTION_NONE') },
  { value: 'lead', label: t('KANBAN.BOARD.STAGE_PROMOTION_LEAD') },
  { value: 'customer', label: t('KANBAN.BOARD.STAGE_PROMOTION_CUSTOMER') },
]);

const stageInitial = stage => stage.name?.[0]?.toUpperCase() || '?';

const resetForm = () => {
  boardName.value = props.board?.name || '';
  boardInboxId.value = props.board?.inbox_id || '';
  stages.value = (props.board?.stages || []).map(stage => ({
    id: stage.id,
    name: stage.name,
    color: stage.color || '#1f93ff',
    position: stage.position || 0,
    settings: { ...(stage.settings || {}) },
  }));
};

watch(() => props.board, resetForm, { immediate: true });

const addDraftStage = () => {
  stages.value.push({
    id: `new-${Date.now()}`,
    name: '',
    color: '#1f93ff',
    position: stages.value.length,
    settings: {},
    isNew: true,
  });
};

const normalizeStagePositions = () => {
  stages.value = stages.value.map((stage, index) => ({
    ...stage,
    position: index,
  }));
};

const moveStage = (stageIndex, direction) => {
  const nextIndex = stageIndex + direction;
  if (nextIndex < 0 || nextIndex >= stages.value.length) return;

  const nextStages = [...stages.value];
  const [stage] = nextStages.splice(stageIndex, 1);
  nextStages.splice(nextIndex, 0, stage);
  stages.value = nextStages;
  normalizeStagePositions();
};

const stagePayload = stage => ({
  stage: {
    name: stage.name.trim(),
    color: stage.color,
    position: stage.position,
    settings: stage.settings || {},
  },
});

const updatePromotion = (stage, value) => {
  stage.settings = {
    ...(stage.settings || {}),
    promote_contact_to: value || null,
  };
};

const saveStage = stage => {
  if (!stage.name.trim()) return;
  if (stage.isNew) {
    emit('createStage', stagePayload(stage));
    return;
  }
  emit('updateStage', stage.id, stagePayload(stage));
};

const deleteStage = stage => {
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(
      t('KANBAN.BOARD.STAGE_DELETE_CONFIRM', { name: stage.name })
    )
  ) {
    return;
  }
  if (stage.isNew) {
    stages.value = stages.value.filter(item => item.id !== stage.id);
    return;
  }
  emit('deleteStage', stage.id);
};

const saveBoard = () => {
  if (!boardName.value.trim()) return;
  emit('saveBoard', {
    board: {
      name: boardName.value.trim(),
      inbox_id: boardInboxId.value || null,
    },
  });
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
    @click.self="emit('close')"
  >
    <section
      class="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-n-weak bg-n-background shadow-xl"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
    >
      <header
        class="flex items-center justify-between border-b border-n-weak px-5 py-4"
      >
        <div class="min-w-0">
          <h2 class="text-lg font-semibold text-n-slate-12">{{ title }}</h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('KANBAN.BOARD.MODAL_DESCRIPTION') }}
          </p>
        </div>
        <button
          type="button"
          class="grid size-10 place-content-center rounded-md text-n-slate-11 transition-colors hover:bg-n-brand/5 hover:text-n-slate-12"
          :aria-label="$t('KANBAN.CANCEL')"
          @click="emit('close')"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </header>

      <div class="min-h-0 overflow-y-auto px-5 py-5">
        <div
          class="grid grid-cols-1 gap-4 rounded-lg border border-n-weak bg-transparent p-4 md:grid-cols-2"
        >
          <label class="block">
            <span class="text-sm font-medium text-n-slate-11">
              {{ $t('KANBAN.BOARD.NAME_LABEL') }}
            </span>
            <input
              v-model="boardName"
              type="text"
              class="mt-1 min-h-11 w-full rounded-md border border-n-weak bg-transparent px-4 py-2.5 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
              :placeholder="$t('KANBAN.BOARD_NAME_PLACEHOLDER')"
            />
          </label>

          <label class="block">
            <span class="text-sm font-medium text-n-slate-11">
              {{ $t('KANBAN.INBOX_LABEL') }}
            </span>
            <select
              v-model="boardInboxId"
              class="mt-1 min-h-11 w-full rounded-md border border-n-weak bg-transparent py-2.5 pl-4 pr-10 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
            >
              <option value="">{{ $t('KANBAN.BOARD.NO_INBOX') }}</option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </label>
        </div>

        <div v-if="isEditing" class="mt-6 border-t border-n-weak pt-5">
          <div class="mb-4 flex items-center justify-between gap-3">
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('KANBAN.BOARD.STAGES_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ $t('KANBAN.BOARD.STAGES_DESCRIPTION') }}
              </p>
            </div>
            <Button
              type="button"
              sm
              faded
              slate
              icon="i-lucide-plus"
              :label="$t('KANBAN.BOARD.ADD_STAGE')"
              @click="addDraftStage"
            />
          </div>

          <div class="overflow-hidden rounded-lg border border-n-weak">
            <div
              class="hidden grid-cols-[96px_1.2fr_88px_120px_200px_96px] gap-3 border-b border-n-weak bg-transparent px-3 py-2 text-xs font-semibold uppercase text-n-slate-10 lg:grid"
            >
              <span>{{ $t('KANBAN.BOARD.STAGE_ORDER_LABEL') }}</span>
              <span>{{ $t('KANBAN.BOARD.STAGE_NAME_LABEL') }}</span>
              <span>{{ $t('KANBAN.BOARD.STAGE_POSITION_LABEL') }}</span>
              <span>{{ $t('KANBAN.BOARD.STAGE_COLOR_LABEL') }}</span>
              <span>{{ $t('KANBAN.BOARD.STAGE_PROMOTION_LABEL') }}</span>
              <span class="text-right">
                {{ $t('KANBAN.BOARD.STAGE_ACTIONS_LABEL') }}
              </span>
            </div>

            <div
              v-for="(stage, index) in stages"
              :key="stage.id"
              class="border-b border-n-weak bg-transparent px-3 py-3 last:border-b-0"
            >
              <div
                class="grid grid-cols-1 gap-3 lg:grid-cols-[96px_1.2fr_88px_120px_200px_96px] lg:items-center"
              >
                <div class="flex items-center gap-1">
                  <Button
                    type="button"
                    xs
                    ghost
                    slate
                    icon="i-lucide-arrow-up"
                    :aria-label="$t('KANBAN.BOARD.STAGE_MOVE_UP')"
                    :disabled="index === 0"
                    @click="moveStage(index, -1)"
                  />
                  <Button
                    type="button"
                    xs
                    ghost
                    slate
                    icon="i-lucide-arrow-down"
                    :aria-label="$t('KANBAN.BOARD.STAGE_MOVE_DOWN')"
                    :disabled="index === stages.length - 1"
                    @click="moveStage(index, 1)"
                  />
                </div>

                <label class="block">
                  <span
                    class="mb-1 block text-xs font-medium text-n-slate-11 lg:hidden"
                  >
                    {{ $t('KANBAN.BOARD.STAGE_NAME_LABEL') }}
                  </span>
                  <div class="flex items-center gap-2">
                    <span
                      class="grid size-9 flex-shrink-0 place-content-center rounded-md border border-n-weak bg-transparent text-xs font-semibold text-n-slate-12"
                    >
                      {{ stageInitial(stage) }}
                    </span>
                    <input
                      v-model="stage.name"
                      type="text"
                      class="min-h-11 w-full rounded-md border border-n-weak bg-transparent px-4 py-2.5 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
                      :placeholder="$t('KANBAN.BOARD.STAGE_NAME_PLACEHOLDER')"
                    />
                  </div>
                </label>

                <label class="block">
                  <span
                    class="mb-1 block text-xs font-medium text-n-slate-11 lg:hidden"
                  >
                    {{ $t('KANBAN.BOARD.STAGE_POSITION_LABEL') }}
                  </span>
                  <input
                    v-model.number="stage.position"
                    type="number"
                    min="0"
                    class="min-h-11 w-full rounded-md border border-n-weak bg-transparent px-4 py-2.5 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
                  />
                </label>

                <label class="block">
                  <span
                    class="mb-1 block text-xs font-medium text-n-slate-11 lg:hidden"
                  >
                    {{ $t('KANBAN.BOARD.STAGE_COLOR_LABEL') }}
                  </span>
                  <div
                    class="flex min-h-11 items-center rounded-md border border-n-weak bg-transparent px-4 py-1.5"
                  >
                    <input
                      v-model="stage.color"
                      type="color"
                      class="h-8 w-full cursor-pointer rounded border-0 bg-transparent p-0"
                      :aria-label="$t('KANBAN.BOARD.STAGE_COLOR_LABEL')"
                    />
                  </div>
                </label>

                <label class="block">
                  <span
                    class="mb-1 block text-xs font-medium text-n-slate-11 lg:hidden"
                  >
                    {{ $t('KANBAN.BOARD.STAGE_PROMOTION_LABEL') }}
                  </span>
                  <select
                    :value="stage.settings?.promote_contact_to || ''"
                    class="min-h-11 w-full rounded-md border border-n-weak bg-transparent py-2.5 pl-4 pr-10 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5"
                    @change="updatePromotion(stage, $event.target.value)"
                  >
                    <option
                      v-for="option in stagePromotionOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </label>

                <div class="flex items-center justify-end gap-1">
                  <Button
                    type="button"
                    sm
                    ghost
                    blue
                    icon="i-lucide-save"
                    :aria-label="$t('KANBAN.BOARD.STAGE_SAVE')"
                    @click="saveStage(stage)"
                  />
                  <Button
                    type="button"
                    sm
                    ghost
                    ruby
                    icon="i-lucide-trash-2"
                    :aria-label="$t('KANBAN.BOARD.STAGE_DELETE')"
                    @click="deleteStage(stage)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer
        class="flex items-center justify-end gap-2 border-t border-n-weak px-5 py-4"
      >
        <Button
          type="button"
          sm
          ghost
          slate
          :label="$t('KANBAN.CANCEL')"
          @click="emit('close')"
        />
        <Button
          type="button"
          sm
          solid
          blue
          :is-loading="isSaving"
          :disabled="!boardName.trim()"
          :label="isEditing ? $t('KANBAN.BOARD.SAVE') : $t('KANBAN.CREATE')"
          @click="saveBoard"
        />
      </footer>
    </section>
  </div>
</template>
